<template>
  <div class="product-table-view">
    <table class="ui very basic table">
      <thead>
        <tr>
          <th class="product-column">PRODUCT</th>
          <th class="sales-column">SALES</th>
          <th class="cogs-column highlighted">COGS</th>
          <th class="margin-column highlighted">MARGIN</th>
          <th class="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="product in paginatedProducts" :key="product.productId" class="product-row">
          <!-- Product Column -->
          <td class="product-cell">
            <div class="product-info">
              <img :src="product.largeImageUrl || '/placeholder.png'" class="product-image" alt="">
              <div class="product-details">
                <i :class="''+product.marketplace.toLowerCase().replace('uk','gb')+' flag'" style="margin-right: 4px;"></i>
                <span class="product-title" @click="showProductDetails(product.productId)">
                  {{ product.productTitle }}
                </span>
              </div>
            </div>
          </td>

          <!-- Sales Column -->
          <td class="sales-cell">
            <div class="sales-info">
              <div>{{ salesCountTotal([product.productId], 'y') }} units sold ystd.</div>
              <div class="sales-today">{{ salesCountTotal([product.productId], 't') }} so far today ({{ product.marketplace.toUpperCase() }})</div>
            </div>
          </td>

          <!-- COGS Column -->
          <td class="cogs-cell">
            <div v-if="product.cogs && product.cogs > 0" class="cogs-value">
              ${{ formatPrice(product.cogs) }}
            </div>
            <div v-else class="cogs-empty">
              <span class="empty-dash">—</span>
              <button class="add-link" @click.stop="openCogsModal(product)">+ Add</button>
            </div>
          </td>

          <!-- Margin Column -->
          <td class="margin-cell">
            <span 
              v-if="product.cogs && product.cogs > 0 && product.buyBoxPrice"
              class="margin-value"
              :class="getMarginClass(calculateMargin(product))">
              {{ calculateMargin(product) }}%
            </span>
            <span v-else class="margin-empty">—</span>
          </td>

          <!-- Actions Column -->
          <td class="actions-cell">
            <button class="ui icon button mini" @click.stop="openChartModal(product)">
              <i class="chart bar icon"></i>
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination -->
    <div class="table-pagination">
      <div class="pagination-info">
        Showing {{ startItem }} - {{ endItem }} of {{ totalItems }} products
      </div>
      <div class="ui pagination menu">
        <a class="item" :class="{ disabled: currentPage === 0 }" @click="prevPage()">
          <i class="left chevron icon"></i>
        </a>
        <a v-for="page in visiblePages" :key="page" class="item" :class="{ active: currentPage === page }" @click="goToPage(page)">
          {{ page + 1 }}
        </a>
        <a class="item" :class="{ disabled: currentPage >= totalPages - 1 }" @click="nextPage()">
          <i class="right chevron icon"></i>
        </a>
      </div>
    </div>

    <!-- COGS Modal -->
    <div class="ui modal" id="tableCogsModal">
      <div class="header">Set COGS for Product</div>
      <div class="content">
        <div class="ui form">
          <div class="field">
            <label>Product</label>
            <p>{{ cogsModalProduct ? cogsModalProduct.productTitle : '' }}</p>
          </div>
          <div class="field">
            <label>COGS (Cost of Goods Sold) $</label>
            <input 
              type="number" 
              step="0.01" 
              min="0" 
              v-model="cogsInput" 
              placeholder="Enter COGS amount"
              @keyup.enter="saveCogs">
          </div>
          <div class="field" v-if="cogsInput && cogsModalProduct && cogsModalProduct.buyBoxPrice">
            <label>Preview</label>
            <p>
              <strong>COGS:</strong> ${{ formatPrice(parseFloat(cogsInput) || 0) }}<br>
              <strong>Buy Box Price:</strong> ${{ formatPrice(cogsModalProduct.buyBoxPrice) }}<br>
              <strong>Margin:</strong> {{ calculateMarginFromCogs(parseFloat(cogsInput) || 0, cogsModalProduct.buyBoxPrice) }}%
            </p>
          </div>
        </div>
      </div>
      <div class="actions">
        <div class="ui cancel button">Cancel</div>
        <div class="ui primary button" @click="saveCogs" :class="{ loading: savingCogs }">
          Save
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from 'jquery'
import { buildProductDetailsQuery } from '../utils/listViewUrlSync.js'

export default {
  name: 'ProductTableView',
  props: {
    products: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      currentPage: 0,
      itemsPerPage: 20,
      cogsModalProduct: null,
      cogsInput: '',
      savingCogs: false
    }
  },
  computed: {
    totalItems() {
      return this.products.length
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage)
    },
    startItem() {
      return this.currentPage * this.itemsPerPage + 1
    },
    endItem() {
      return Math.min((this.currentPage + 1) * this.itemsPerPage, this.totalItems)
    },
    paginatedProducts() {
      const start = this.currentPage * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.products.slice(start, end)
    },
    visiblePages() {
      const pages = []
      const maxVisible = 5
      let startPage = Math.max(0, this.currentPage - 2)
      let endPage = Math.min(this.totalPages - 1, startPage + maxVisible - 1)
      
      if (endPage - startPage < maxVisible - 1) {
        startPage = Math.max(0, endPage - maxVisible + 1)
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  methods: {
    formatPrice(price) {
      if (!price && price !== 0) return '0.00'
      return parseFloat(price).toFixed(2)
    },
    calculateMargin(product) {
      if (!product.cogs || !product.buyBoxPrice || product.buyBoxPrice === 0) return 0
      const margin = ((product.buyBoxPrice - product.cogs) / product.buyBoxPrice) * 100
      return margin.toFixed(0)
    },
    calculateMarginFromCogs(cogs, buyBoxPrice) {
      if (!cogs || !buyBoxPrice || buyBoxPrice === 0) return '0'
      const margin = ((buyBoxPrice - cogs) / buyBoxPrice) * 100
      return margin.toFixed(0)
    },
    getMarginClass(margin) {
      const m = parseFloat(margin)
      if (m > 20) return 'margin-good'
      if (m >= 15) return 'margin-ok'
      return 'margin-poor'
    },
    salesCountTotal(productIds, t) {
      const getSales = id => {
        const product = this.$parent.$parent.$parent.worldStore.products[id]
        if (product && product.salesToday !== undefined && product.salesYesterday !== undefined) {
          return {
            today: product.salesToday,
            yesterday: product.salesYesterday
          }
        }
        return null
      }
      if (t === 'y') return productIds.reduce((prevVal, id) => prevVal + (getSales(id) !== null ? getSales(id).yesterday : 0), 0)
      if (t === 't') return productIds.reduce((prevVal, id) => prevVal + (getSales(id) !== null ? getSales(id).today : 0), 0)
      return 0
    },
    showProductDetails(productId) {
      const cleanId = String(productId).replace(/^PROD-/i, '')
      this.$router.push({ path: '/product_details', query: buildProductDetailsQuery(cleanId, this.$route.fullPath) })
    },
    openCogsModal(product) {
      this.cogsModalProduct = product
      this.cogsInput = product.cogs ? String(product.cogs) : ''
      $('#tableCogsModal').modal('show')
    },
    saveCogs() {
      if (!this.cogsModalProduct || !this.cogsInput) return
      
      const cogsValue = parseFloat(this.cogsInput)
      if (isNaN(cogsValue) || cogsValue < 0) {
        alert('Please enter a valid COGS amount')
        return
      }

      this.savingCogs = true
      const productId = this.cogsModalProduct.productId
      const cleanId = String(productId).replace(/^PROD-/i, '')
      
      this.$http.put('/api/v1/products/' + cleanId, {
        cogs: cogsValue
      }).then((response) => {
        // Update the product in worldStore
        if (this.$parent.$parent.$parent.worldStore && this.$parent.$parent.$parent.worldStore.products) {
          this.$set(this.$parent.$parent.$parent.worldStore.products[productId], 'cogs', cogsValue)
        }
        
        $('#tableCogsModal').modal('hide')
        this.savingCogs = false
        this.cogsModalProduct = null
        this.cogsInput = ''
      }, (response) => {
        this.savingCogs = false
        const errorMsg = response.body && response.body.error ? response.body.error : 'Failed to save COGS'
        alert('Error: ' + errorMsg)
      })
    },
    openChartModal(product) {
      // Emit event to parent to open chart modal
      this.$emit('open-chart', product)
    },
    prevPage() {
      if (this.currentPage > 0) {
        this.currentPage--
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages - 1) {
        this.currentPage++
      }
    },
    goToPage(page) {
      this.currentPage = page
    }
  },
  mounted() {
    this.$nextTick(() => {
      $('#tableCogsModal').modal({
        onHide: () => {
          this.cogsModalProduct = null
          this.cogsInput = ''
        }
      })
    })
  }
}
</script>

<style scoped>
.product-table-view {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.ui.very.basic.table {
  border: none;
  margin: 0;
}

.ui.very.basic.table thead th {
  background: #f9fafb;
  color: #6b7280;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px;
  border-bottom: 2px solid #e5e7eb;
}

.ui.very.basic.table thead th.highlighted {
  background: #dbeafe;
  color: #3b82f6;
}

.product-row {
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;
}

.product-row:hover {
  background: #f9fafb;
}

.product-row td {
  padding: 16px;
  vertical-align: middle;
}

/* Product Column */
.product-cell {
  max-width: 400px;
}

.product-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-image {
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  flex-shrink: 0;
}

.product-details {
  flex: 1;
  min-width: 0;
}

.product-title {
  color: #3b82f6;
  cursor: pointer;
  font-size: 14px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-title:hover {
  text-decoration: underline;
}

/* Sales Column */
.sales-info {
  font-size: 13px;
  color: #374151;
}

.sales-today {
  color: #6b7280;
  font-size: 12px;
  margin-top: 4px;
}

/* COGS Column */
.cogs-cell {
  min-width: 120px;
}

.cogs-value {
  font-weight: 600;
  color: #374151;
  font-size: 14px;
}

.cogs-empty {
  display: flex;
  align-items: center;
  gap: 8px;
}

.empty-dash {
  color: #d1d5db;
  font-size: 16px;
}

.add-link {
  color: #3b82f6;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  text-decoration: none;
}

.add-link:hover {
  text-decoration: underline;
}

/* Margin Column */
.margin-cell {
  min-width: 100px;
}

.margin-value {
  font-weight: 600;
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
}

.margin-value.margin-good {
  color: #059669;
  background: #d1fae5;
}

.margin-value.margin-ok {
  color: #d97706;
  background: #fef3c7;
}

.margin-value.margin-poor {
  color: #dc2626;
  background: #fee2e2;
}

.margin-empty {
  color: #d1d5db;
  font-size: 16px;
}

/* Actions Column */
.actions-cell {
  text-align: right;
  width: 60px;
}

/* Pagination */
.table-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.pagination-info {
  color: #6b7280;
  font-size: 13px;
}

.ui.pagination.menu {
  margin: 0;
  box-shadow: none;
  border: none;
}

.ui.pagination.menu .item {
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  margin: 0 2px;
  border-radius: 4px;
  font-size: 13px;
  color: #374151;
}

.ui.pagination.menu .item:hover:not(.disabled) {
  background: #f3f4f6;
}

.ui.pagination.menu .item.active {
  background: #3b82f6;
  color: #ffffff;
  border-color: #3b82f6;
}

.ui.pagination.menu .item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>




// WEBPACK FOOTER //
// ProductTableView.vue?21767652